import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';

// For testing: 30 seconds timeout, 10 seconds warning
// const INACTIVITY_TIMEOUT = 30 * 1000; // 30 seconds for testing
// const WARNING_BEFORE_LOGOUT = 10 * 1000; // 10 seconds warning

// Production: 20 minutes timeout, 2 minutes warning
const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes in milliseconds
const WARNING_BEFORE_LOGOUT = 2 * 60 * 1000; // Show warning 2 minutes before logout

const InactivityLogout = () => {
  const { user, logout } = useAuth();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const countdownRef = useRef(null);
  const isLoggingOutRef = useRef(false);

  const performLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    
    // Clear all timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setShowWarning(false);
    
    try {
      await logout();
    } catch (e) {
      console.error('Logout error:', e);
    }
    
    // Force redirect to login page
    window.location.replace('/login?reason=inactivity');
  }, [logout]);

  const resetTimer = useCallback(() => {
    // Don't reset if already logging out
    if (isLoggingOutRef.current) return;
    
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setShowWarning(false);

    // Only set timers if user is logged in
    if (!user) return;

    // Set warning timer (shows 2 minutes before logout)
    warningTimeoutRef.current = setTimeout(() => {
      if (isLoggingOutRef.current) return;
      
      setShowWarning(true);
      setRemainingTime(Math.floor(WARNING_BEFORE_LOGOUT / 1000));
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      performLogout();
    }, INACTIVITY_TIMEOUT);
    
  }, [user, performLogout]);

  const handleStayLoggedIn = useCallback(() => {
    setShowWarning(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    resetTimer();
  }, [resetTimer]);

  // Set up activity listeners when user is logged in
  useEffect(() => {
    if (!user) {
      // Clean up timers if user logs out
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      isLoggingOutRef.current = false;
      return;
    }

    // Reset logging out flag when user logs in
    isLoggingOutRef.current = false;

    const activityEvents = [
      'mousedown',
      'mousemove', 
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
      'keypress'
    ];

    // Throttle to avoid excessive resets
    let lastActivity = 0;
    const handleActivity = () => {
      const now = Date.now();
      // Only reset timer if at least 5 seconds since last activity
      if (now - lastActivity > 5000) {
        lastActivity = now;
        resetTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [user, resetTimer]);

  // Don't render anything if user is not logged in
  if (!user) return null;

  // Warning modal
  if (showWarning) {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    
    return (
      <div 
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-[99999]"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl transform transition-all">
          <div className="text-center">
            {/* Clock Icon */}
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Session Expiring Soon</h2>
            
            {/* Message */}
            <p className="text-gray-600 mb-6 text-lg">
              You will be logged out in{' '}
              <span className="font-bold text-amber-600 text-xl">
                {minutes}:{seconds.toString().padStart(2, '0')}
              </span>
              {' '}due to inactivity.
            </p>
            
            {/* Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleStayLoggedIn}
                className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-all transform hover:scale-105 shadow-lg"
              >
                Stay Logged In
              </button>
              <button
                onClick={performLogout}
                className="px-8 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-xl transition-all"
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InactivityLogout;
